import perspectiveTracking from '$lib/global/core/perspective-tracking-api';

import { flatten } from 'flat';
import { nanoid } from 'nanoid/non-secure';

import events from './events';
import { getNowISO } from './helper';
import { getTrackingService } from './TrackingServiceInstance';

/**
 * Deliver funnel events without analytics.js.
 *
 * Perspective's own analytics normally reaches `/ps-fnl-events/api/data/*` *through*
 * Segment: `window.analytics.track()` fans out to the registered integrations, one of which
 * is our own `PS_TRACKING` (`global/core/ps-integration.ts`), which forwards the normalized
 * message to `global/core/perspective-tracking-api.ts`.
 *
 * That is fine for a visitor who consented, but it makes analytics.js a hard dependency of
 * our own measurement — and analytics.js sets `ajs_*` cookies the moment it loads. For a
 * visitor who rejected the banner we want the measurement without the dependency, so this
 * module builds the same message shape by hand and calls `perspective-tracking-api`
 * directly. No Segment script, no third-party integration, same wire contract.
 *
 * Kept deliberately close to `ps-integration.ts`: if that file changes shape, this one has
 * to follow. The duplication is the price of not loading a 100kB third-party script to talk
 * to our own origin.
 */

type Context = Record<string, unknown>;

/**
 * The envelope Segment would otherwise generate.
 *
 * `messageId` is what the backend dedups `Activity` documents on
 * (lemon-worker `track/v4` looks up `content.messageId`), so it must be unique per event.
 * The `ps-` prefix distinguishes these from Segment's own `ajs-` ids in the data.
 */
const envelope = () => {
    const now = getNowISO();
    const { userId } = getTrackingService();

    return {
        messageId: `ps-${nanoid()}`,
        timestamp: now,
        sentAt: now,
        userId,
        anonymousId: userId,
    };
};

export const dispatchTrack = (
    event: string,
    properties: Record<string, unknown> | undefined,
    context: Context,
): void => {
    perspectiveTracking.track({
        ...envelope(),
        event,
        properties: properties ?? {},
        context,
    });
};

/**
 * Mirrors `PS_TRACKING.prototype.page`, which rewrites a Segment `page` call into an event
 * named `Page Viewed` and narrows the properties to the four the backend cares about. The
 * A/B variant report counts these by `pageId`, so the shape matters.
 */
export const dispatchPage = (
    pageName: string,
    properties: Record<string, unknown>,
    context: Context,
): void => {
    const { pageId, pageSlug, isLastPage } = properties;

    perspectiveTracking.page({
        ...envelope(),
        name: pageName,
        event: events.pageLoad,
        properties: { pageId, pageName, pageSlug, isLastPage },
        context,
    });
};

/**
 * Mirrors `PS_TRACKING.prototype.identify`, including the duplicate guard — without it a
 * single form submit would deliver the same traits twice, because `updateVisitorProperties`
 * identifies once for the values and once more when it stamps the conversion.
 */
export const dispatchIdentify = (
    traits: Record<string, unknown> | undefined,
    context: Context,
): void => {
    const service = getTrackingService();
    const message = {
        ...envelope(),
        traits: flatten({ ...(traits ?? {}) }) as Record<string, unknown>,
        context,
    };

    if (service.getSession().isIdentifyDuplicate(message)) {
        return;
    }

    perspectiveTracking.identify(message);
};
